import type { Transition, Variants } from 'framer-motion'

/* The system's motion vocabulary, in one place.
 *
 * Motion here is short, flat and never decorative:
 * no bounce, no spring overshoot, no scale flourish. So these are the SAME
 * curves and durations already declared in tokens.css, restated in the numeric
 * form Framer needs — CSS custom properties cannot be read by the animation
 * engine, and a `var(--dur-base)` string would silently animate for 0ms.
 *
 * Keep the two in sync: if a duration changes in tokens.css it must change
 * here, and vice versa. That duplication is deliberate and narrow — four
 * numbers and three curves — and it buys the one thing plain CSS animation
 * could not give this app: an EXIT transition. The previous drawer and sheet
 * unmounted the instant `open` went false, so a dismissal had no motion at all
 * while an entrance did. AnimatePresence is what closes that asymmetry.
 */

/** Mirrors --dur-* in tokens.css, in seconds. */
export const duration = {
  instant: 0.08,
  fast: 0.14,
  base: 0.2,
  slow: 0.32,
} as const

/** Mirrors --ease-* in tokens.css as cubic-bezier control points. */
export const ease = {
  standard: [0.2, 0, 0, 1],
  out: [0.16, 1, 0.3, 1],
  in: [0.4, 0, 1, 1],
} as const satisfies Record<string, [number, number, number, number]>

/** Entering: decelerate into place. Leaving: accelerate away, and faster —
 *  a dismissal the user already asked for should not make them wait. */
export const enterTransition: Transition = {
  duration: duration.base,
  ease: ease.out,
}

export const exitTransition: Transition = {
  duration: duration.fast,
  ease: ease.in,
}

/** The scrim behind any overlay. Opacity only — a scrim that moves is a scrim
 *  drawing attention to itself. */
export const scrimVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: duration.base, ease: ease.standard },
  },
  exit: { opacity: 0, transition: exitTransition },
}

/** The right-hand drawer, entering from its own edge. Translation only: an
 *  overlay that arrives from off-screen has already said where it came from,
 *  so adding a fade would be saying it twice. */
export const drawerVariants: Variants = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: enterTransition },
  exit: { x: '100%', transition: exitTransition },
}

/** The mobile navigation drawer — the same idea from the left edge. */
export const sidebarVariants: Variants = {
  hidden: { x: '-100%' },
  visible: { x: 0, transition: enterTransition },
  exit: { x: '-100%', transition: exitTransition },
}

/** The bottom sheet on touch: rises from the edge it is anchored to. */
export const sheetMobileVariants: Variants = {
  hidden: { y: '100%' },
  visible: { y: 0, transition: enterTransition },
  exit: { y: '100%', transition: exitTransition },
}

/** The same component as a centred desktop dialog. It is anchored to nothing,
 *  so it cannot travel from an edge; it lifts a few pixels and fades instead.
 *  6px matches the asa-rise keyframe this replaces. No scale — a dialog that
 *  zooms reads as a notification, not as a layer over a live screen. */
export const sheetDesktopVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: enterTransition },
  exit: { opacity: 0, y: 6, transition: exitTransition },
}

/** A popover anchored to the control that opened it — the language select.
 *  It is already attached to its trigger, so it needs no travel to say where it
 *  came from: 2px and a fade at --dur-fast, the shortest move in the system.
 *  A menu is opened deliberately and read immediately; anything longer puts an
 *  animation between a click and a two-line list. */
export const menuVariants: Variants = {
  hidden: { opacity: 0, y: -2 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.fast, ease: ease.out },
  },
  exit: { opacity: 0, y: -2, transition: { duration: duration.instant, ease: ease.in } },
}

/** Tab panels. A section switch is a smaller move than a navigation — the
 *  chrome around it never leaves — so it gets less motion, not more: a fade
 *  and 3px at --dur-fast. Like pages, there is no exit state; the outgoing
 *  panel is replaced rather than animated away, so the new section's data is
 *  on screen immediately. */
export const tabPanelVariants: Variants = {
  hidden: { opacity: 0, y: 3 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.fast, ease: ease.out },
  },
}

/** The shared layoutId for the active-tab marker. Framer matches the marker
 *  across tabs by this id and interpolates its position, which is what makes
 *  the underline appear to slide between sections rather than cut. Any tab set
 *  rendering its own marker needs its OWN id — two tab bars sharing one string
 *  would animate the marker between them, across the page. */
export const TAB_MARKER_TRANSITION: Transition = {
  duration: duration.base,
  ease: ease.standard,
}

/** A column of cards arriving together, one just behind the next.
 *
 * The parent animates nothing itself — it exists only to time its children, so
 * a page does not have to hand each card an index and a hand-computed delay.
 *
 * 40ms is the whole budget per step, and the list it drives is short. A stagger
 * long enough to WATCH is a stagger that has become the point; this one is only
 * meant to give the eye an order to read the page in — identity, then what you
 * can change, then what you cannot. By the time the pointer reaches the first
 * control everything has landed.
 *
 * delayChildren holds the first card until the page transition it is nested
 * inside has finished its own 140ms entrance, so the two moves read as one
 * arrival rather than as a card fighting its way out of a fading page. */
export const staggerParentVariants: Variants = {
  hidden: {},
  visible: {
    transition: { delayChildren: duration.fast, staggerChildren: 0.04 },
  },
}

/** One card in that column. Deliberately the same fade-plus-a-few-pixels as
 *  every other entrance in the system — 6px, matching the desktop sheet. No
 *  scale: a card that zooms in reads as a notification arriving, and these were
 *  always going to be here. */
export const staggerChildVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.base, ease: ease.out },
  },
}

/** Routed pages. The shortest motion in the system by some margin.
 *
 * A page transition is the one animation a user cannot opt out of by not
 * clicking, and they see it on every single navigation — so it is held to a
 * fade plus 4px of travel. Anything longer or larger puts a delay between the
 * click and the data on a screen whose whole job is dense operational readouts.
 *
 * The exit is 100ms — deliberately shorter than the 140ms entrance and faster
 * than --dur-fast, which is why it is spelled out rather than reusing
 * exitTransition. Under AnimatePresence mode="wait" the two run in SEQUENCE,
 * so the pair is the floor on how long any navigation can feel: 240ms total.
 * Every millisecond added here is paid on every click.
 *
 * The outgoing page leaves DOWNWARD (+4px) while the incoming one arrives from
 * above (-4px is not used; it enters at +4 and settles to 0). Both move in the
 * same direction, so a navigation reads as one sheet of paper replacing
 * another rather than as two unrelated fades. */
export const pageVariants: Variants = {
  hidden: { opacity: 0, y: 4 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.fast, ease: ease.out },
  },
  exit: {
    opacity: 0,
    y: 4,
    transition: { duration: 0.1, ease: ease.in },
  },
}
